import { Injectable, computed, inject } from '@angular/core';

import { AccountState } from 'src/app/store';

/**
 * The active partner's policy on how students get onto a caseload.
 *
 * Two capabilities, one place, because every "add a student" affordance has to
 * agree: the caseload toolbar modal, the home panel modal, the notebook shell
 * action button, and the route guard on the add page.
 *
 * ── Why every flag is phrased as a GRANT ─────────────────────────────────
 * A toggle already carries polarity, so a negated label ("Block notes: ON")
 * forces the reader through a double negative. Every partner flag therefore
 * reads as something the district ALLOWS, and the UI toggles match.
 *
 * ── Why the defaults differ ──────────────────────────────────────────────
 * The default encodes what was true BEFORE each flag existed, so no partner
 * changes behaviour on the day a flag ships and nothing needs backfilling:
 *
 *   - `allowStudentCreation` defaults TRUE  — creation predates the flag.
 *   - `allowStudentLookup`   defaults FALSE — lookup was a new capability
 *                                             districts opt into.
 *
 * **These are UX policies, not security boundaries.** Firestore rules still
 * let any active member write `partners/{id}/students/{id}`. They stop the
 * affordance, not a determined client. Making either a real boundary needs a
 * rules change that reads the partner doc on every student write.
 *
 * @security FERPA - gates how student records are created and surfaced
 */
@Injectable({ providedIn: 'root' })
export class StudentRosterPolicy {
  private readonly accountState = inject(AccountState);

  /**
   * True when a student may be created from the Notebook.
   *
   * Deliberately NOT exempt for administrators. Turning this off moves student
   * creation into the Administration portal, and `/admin/{partner}/students/add`
   * is a separate route behind `partnerAdminGuard` that this flag never
   * touches — so an admin keeps the capability, just not from the practitioner
   * surface. Exempting them only produced a Notebook button that contradicted
   * the district's own setting.
   */
  readonly canCreateStudent = computed<boolean>(() => this.accountState.activeTeam()?.allowStudentCreation ?? true);

  /**
   * True when practitioners may browse the partner roster and pull existing
   * students into their own caseload.
   *
   * Independent of the admin portal's student access — admins can always see
   * and manage every student regardless.
   */
  readonly canLookupStudents = computed<boolean>(() => this.accountState.activeTeam()?.allowStudentLookup ?? false);

  /** Tooltip for the disabled Create affordance. Empty when creation is allowed. */
  readonly createBlockedReason = computed<string>(() =>
    this.canCreateStudent() ? '' : 'This district creates students in the Administration portal.',
  );

  /** Tooltip for the disabled Lookup affordance. Empty when lookup is allowed. */
  readonly lookupBlockedReason = computed<string>(() =>
    this.canLookupStudents() ? '' : 'Only admins can add existing students to your caseload at this time.',
  );
}
